import React, { useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { CallViewsByDay } from "../../../types/call-views.types";
import { formatNumber } from "../../../utils/formatters";

interface ByDayChartProps {
  data: CallViewsByDay[];
}

const SERIES: { key: "web" | "api" | "email"; label: string; color: string }[] = [
  { key: "web", label: "Web", color: "bg-primary-500" },
  { key: "api", label: "API", color: "bg-emerald-500" },
  { key: "email", label: "Email", color: "bg-amber-500" },
];

const formatDate = (dateStr: string): string => {
  try {
    return format(parseISO(dateStr), "MMM d");
  } catch {
    return dateStr;
  }
};

export const ByDayChart: React.FC<ByDayChartProps> = ({ data }) => {
  const [activeTooltip, setActiveTooltip] = useState<number | null>(null);

  const { bars, maxTotal } = useMemo(() => {
    if (!data.length) return { bars: [], maxTotal: 0 };
    const computed = data.map((d) => ({
      ...d,
      total: d.web + d.api + d.email,
    }));
    const max = Math.max(...computed.map((d) => d.total), 0);
    return { bars: computed, maxTotal: max };
  }, [data]);

  if (!data.length) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-4">
          Odsłony dzienne
        </h3>
        <div className="text-neutral-500 text-center py-8">
          Brak danych dla wybranego okresu
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-lg border border-neutral-200 p-6"
      data-testid="chart-by-day"
      role="figure"
      aria-label="Wykres dziennych odsłon naborów w podziale na kanały"
    >
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-semibold text-neutral-900">
          Odsłony dzienne
        </h3>
        <div className="flex items-center gap-4 text-xs text-neutral-600">
          {SERIES.map((s) => (
            <span key={s.key} className="flex items-center gap-1.5">
              <span className={`inline-block w-3 h-3 rounded-sm ${s.color}`} />
              {s.label}
            </span>
          ))}
        </div>
      </div>

      <div className="relative flex gap-1 h-48">
        {/* Y-axis */}
        <div className="flex flex-col justify-between text-xs text-neutral-500 pr-2 py-0.5">
          <span>{formatNumber(maxTotal)}</span>
          <span>{formatNumber(Math.round(maxTotal / 2))}</span>
          <span>0</span>
        </div>

        {/* Chart area */}
        <div className="flex-1 relative h-full">
          <div className="flex items-end gap-1 h-full">
            {bars.map((bar, index) => {
              const totalPct = maxTotal > 0 ? (bar.total / maxTotal) * 100 : 0;
              return (
                <div
                  key={bar.date}
                  className="flex-1 h-full flex flex-col justify-end items-center relative"
                  data-testid={`bar-${index}`}
                >
                  {/* Tooltip */}
                  <div
                    className={`absolute bottom-full mb-2 z-10 transition-opacity ${
                      activeTooltip === index
                        ? "opacity-100"
                        : "opacity-0 pointer-events-none"
                    }`}
                  >
                    <div className="bg-neutral-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                      <div className="font-medium">{formatDate(bar.date)}</div>
                      <div>Web: {formatNumber(bar.web)}</div>
                      <div>API: {formatNumber(bar.api)}</div>
                      <div>Email: {formatNumber(bar.email)}</div>
                      <div className="border-t border-neutral-700 mt-0.5 pt-0.5">
                        Razem: {formatNumber(bar.total)}
                      </div>
                    </div>
                  </div>

                  {/* Stacked bar */}
                  <div
                    tabIndex={0}
                    role="img"
                    aria-label={`${formatDate(bar.date)}: web ${bar.web}, api ${bar.api}, email ${bar.email}, razem ${bar.total}`}
                    className="w-full flex flex-col justify-end cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 rounded-t overflow-hidden"
                    style={{ height: `${Math.max(totalPct, 2)}%` }}
                    onMouseEnter={() => setActiveTooltip(index)}
                    onMouseLeave={() => setActiveTooltip(null)}
                    onFocus={() => setActiveTooltip(index)}
                    onBlur={() => setActiveTooltip(null)}
                    onTouchStart={() => setActiveTooltip(index)}
                    onTouchEnd={() => setActiveTooltip(null)}
                  >
                    {SERIES.map((s) => {
                      const value = bar[s.key];
                      if (!bar.total || !value) return null;
                      const segPct = (value / bar.total) * 100;
                      return (
                        <div
                          key={s.key}
                          className={s.color}
                          style={{ height: `${segPct}%` }}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* X-axis labels */}
      <div className="flex justify-between mt-2 text-xs text-neutral-500 ml-12">
        {bars.length > 0 && (
          <>
            <span>{formatDate(bars[0].date)}</span>
            {bars.length > 2 && (
              <span>{formatDate(bars[Math.floor(bars.length / 2)].date)}</span>
            )}
            {bars.length > 1 && (
              <span>{formatDate(bars[bars.length - 1].date)}</span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ByDayChart;
